import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/popover/x-dropdown-selection.js';
import type { XDropdownSelection } from '@adornis/popover/x-dropdown-selection.js';
import '@adornis/popover/x-tooltip.js';
import { subscribeAllOSCEGroups } from '@ils/osces/api/osce-group.js';
import '@ils/osces/client/ils-osce-picker.js';
import '@ils/osces/client/ils-osce-type.js';
import type { OSCEGroup } from '@ils/osces/db/osce-group.js';
import { OSCE } from '@ils/osces/db/osce.js';
import { ExamType, type OSCECategory, OsceCategoriesByExamType, prettyOsceCategory } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, distinctUntilKeyChanged, filter, map, switchMap } from 'rxjs';
import { type ExamDay, OsceTesterTuple } from '../../db/examdays.js';

/**
 * @element ils-editor-exam-osce-selection
 */
@customElement('ils-editor-exam-osce-selection')
export class IlsEditorExamOsceSelection extends ChemistryLitElement {
  @property({ attribute: false }) examDay = new RXController<ExamDay | null>(this, null);
  @property({ attribute: false }) allOSCEs = new RXController<OSCE[]>(this, []);
  @state() private readonly remainingOsces = new RXController(
    this,
    combineLatest([this.allOSCEs.observable, this.examDay.observable.pipe(filter(Boolean))]).pipe(
      map(([osces, examDay]) =>
        osces.reduce(
          (acc, osce) => {
            if (examDay.osceTesterTuples.some(tuple => tuple.osceID === osce._id)) acc.assigned.push(osce);
            else acc.remaining.push(osce);
            return acc;
          },
          { assigned: [] as OSCE[], remaining: [] as OSCE[] },
        ),
      ),
    ),
  );

  private readonly allOSCEGroups = this.examDay.observable.pipe(
    filter(Boolean),
    distinctUntilKeyChanged('_id'),
    switchMap(examDay =>
      subscribeAllOSCEGroups(
        examDay.examType ?? ExamType.FAMED,
        false,
      )({ _id: 1, name: 1, osceTypes: 1, archivedAt: 1 }),
    ),
  );
  @state() private readonly remainingOsceGroups = new RXController(
    this,
    combineLatest([this.allOSCEGroups, this.examDay.observable.pipe(filter(Boolean))]),
    obs$ =>
      obs$.pipe(
        map(([osceGroups, examDay]) =>
          osceGroups.reduce(
            (acc, osceGroup) => {
              if (examDay.osceTesterTuples.map(osceTuple => osceTuple.osceGroupID).includes(osceGroup._id))
                acc.assigned.push(osceGroup);
              else acc.remaining.push(osceGroup);
              return acc;
            },
            { assigned: [] as OSCEGroup[], remaining: [] as OSCEGroup[] },
          ),
        ),
      ),
  );

  @state() private _selectedOsceType?: Maybe<OSCECategory | 'OSCE-Gruppe'>;

  override render() {
    const examDay = this.examDay.value;
    if (!examDay) return nothing;

    const oscesOrGroups = examDay.osceTesterTuples.reduce<
      Array<OsceTesterTuple | { osceGroupID: string; osceTesterTuples: OsceTesterTuple[] }>
    >((acc, curr) => {
      if (!curr.osceGroupID) {
        acc.push(curr);
        return acc;
      }

      const groupEntry = acc.find(osceOrGroup =>
        osceOrGroup instanceof OsceTesterTuple ? false : osceOrGroup.osceGroupID === curr.osceGroupID,
      ) as { osceGroupID: string; osceTesterTuples: OsceTesterTuple[] } | undefined;
      if (groupEntry) groupEntry.osceTesterTuples.push(curr);
      else acc.push({ osceGroupID: curr.osceGroupID!, osceTesterTuples: [curr] });

      return acc;
    }, []);

    const osceTypeSelection = createRef<XDropdownSelection<string>>();
    return html`
      <x-flex space="md">
        <x-h3> OSCE Auswahl (${examDay.osceTesterTuples.length} zugewiesene OSCEs) </x-h3>
        <x-flex space="sm">
          ${repeat(oscesOrGroups, oscesOrGroups =>
            oscesOrGroups instanceof OsceTesterTuple
              ? this._displayPlainOsceTesterTuple(oscesOrGroups)
              : this._displayGroupOsceTesterTuple(oscesOrGroups),
          )}
        </x-flex>
        <x-hr> </x-hr>
        <x-flex space="md">
          <x-text> OSCE(-Gruppe) hinzufügen </x-text>
          <x-flex space="sm">
            <x-dropdown-selection
              flex
              placeholder="Typ Auswählen"
              .selectables=${[...OsceCategoriesByExamType[examDay.examType ?? ExamType.FAMED], 'OSCE-Gruppe']}
              .renderString=${(osceCategory?: OSCECategory | 'OSCE-Gruppe') =>
                osceCategory === 'OSCE-Gruppe' ? 'OSCE-Gruppe' : osceCategory ? prettyOsceCategory(osceCategory) : ''}
              @value-picked=${(e: ValueEvent<Maybe<OSCECategory | 'OSCE-Gruppe'>>) =>
                (this._selectedOsceType = e.detail.value)}
              ${ref(osceTypeSelection)}
            >
            </x-dropdown-selection>
            <x-dropdown-selection
              flex
              placeholder="OSCE/Gruppe Auswählen"
              .search=${(search: string) => {
                if (this._selectedOsceType === 'OSCE-Gruppe') {
                  return this.remainingOsceGroups.value?.remaining.filter(osceGroup =>
                    search ? osceGroup.name?.includes(search) : true,
                  );
                } else {
                  const filteredOsces = this.remainingOsces.value?.remaining.filter(osce => {
                    if (!this._selectedOsceType) return !osce.archivedAt;
                    return osce.osceType === this._selectedOsceType && !osce.archivedAt;
                  });

                  return filteredOsces?.filter(osce =>
                    search ? osce.title.toLowerCase().includes(search.toLowerCase()) : true,
                  );
                }
              }}
              .renderString=${(osce: OSCE | OSCEGroup) =>
                osce
                  ? osce instanceof OSCE
                    ? this._osceDropdownContent(examDay, osce)
                    : this._osceGroupDropdownContent(examDay, osce)
                  : ''}
              @value-picked=${(e: ValueEvent<OSCE | OSCEGroup>) => {
                if (e.detail.value instanceof OSCE) {
                  if (examDay.osceTesterTuples.find(osceTuple => osceTuple.osceID === e.detail.value._id)) return;
                  examDay.osceTesterTuples.push(
                    new OsceTesterTuple({
                      osceID: e.detail.value._id,
                      osce: e.detail.value,
                      testerIDs: Array(10).fill(null),
                    }),
                  );
                } else {
                  if (examDay.osceTesterTuples.find(osceTuple => osceTuple.osceGroupID === e.detail.value._id)) return;
                  examDay.osceTesterTuples = examDay.osceTesterTuples.concat(
                    Array.from(
                      { length: e.detail.value.osceTypes?.length ?? 0 },
                      (_, i) =>
                        new OsceTesterTuple({
                          osceGroupID: e.detail.value._id,
                          osceGroupIndex: i,
                          testerIDs: new Array(10).fill(''),
                        }),
                    ),
                  );
                }

                (e.currentTarget as XDropdownSelection<OSCE | OSCEGroup> | undefined)?.value.next(null);
                osceTypeSelection.value?.value.next(null);
              }}
              .emptyState=${html`<x-text>Keine OSCEs / OSCE-Gruppen gefunden</x-text>`}
            ></x-dropdown-selection>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  // .selectables=${this._selectedOsceType === 'OSCE-Gruppe'
  //   ? this.remainingOsceGroups.value?.remaining
  //   : this.remainingOsces.value?.remaining.filter(osce => {
  //       if (!this._selectedOsceType) return !osce.archivedAt;
  //       return osce.osceType === this._selectedOsceType && !osce.archivedAt;
  //     }) ?? []}

  private _displayPlainOsceTesterTuple(osceTesterTuple: OsceTesterTuple) {
    const examDay = this.examDay.value;
    if (!examDay) return nothing;

    const osce =
      osceTesterTuple.osceID === osceTesterTuple.osce?._id
        ? osceTesterTuple.osce
        : this.remainingOsces.value?.assigned?.find(osce => osce._id === osceTesterTuple.osceID);

    return html`
      <x-basic-card padding="sm" space="md">
        <x-flex horizontal crossaxis-center space-between space="md">
          <x-text> <b>OSCE</b>: ${osce?.osceType ? prettyOsceCategory(osce.osceType) : ''} </x-text>
          <x-button
            mode="icon"
            icon="delete"
            tone="error"
            @click=${() => {
              const index = examDay.osceTesterTuples.findIndex(
                osceTuple => osceTuple.osceID === osceTesterTuple.osceID,
              );
              if (index < 0) return;
              examDay.osceTesterTuples.splice(index, 1);
            }}
          ></x-button>
        </x-flex>
        <ils-osce-picker
          .value=${osceTesterTuple.osce}
          mode="entity"
          .examDay=${examDay}
          .allOSCEs=${this.allOSCEs.value}
          .osceFilter=${(o: OSCE) =>
            o.osceType === osce?.osceType &&
            this.remainingOsces.value?.assigned.every(osce => osce._id !== o._id) &&
            !o.archivedAt}
          @value-picked=${(e: ValueEvent<Maybe<OSCE>>) => {
            osceTesterTuple.osceID = e.detail.value?._id ?? null;
            osceTesterTuple.osce = e.detail.value;
          }}
        ></ils-osce-picker>
      </x-basic-card>
    `;
  }

  private _displayGroupOsceTesterTuple(groupedOsceTesterTuple: {
    osceGroupID: string;
    osceTesterTuples: OsceTesterTuple[];
  }) {
    const examDay = this.examDay.value;
    if (!examDay) return nothing;

    const group = (this.remainingOsceGroups.value?.assigned ?? []).find(
      group => group._id === groupedOsceTesterTuple.osceGroupID,
    );
    if (!group) return nothing;

    return html`
      <x-basic-card padding="sm" space="md">
        <x-flex horizontal crossaxis-center space-between space="md">
          <x-text>
            <b>OSCE-Gruppe</b>:
            ${this.remainingOsceGroups.value?.assigned.find(group => group._id === groupedOsceTesterTuple.osceGroupID)
              ?.name ?? ''}
          </x-text>
          <x-button
            mode="icon"
            icon="delete"
            tone="error"
            @click=${() => {
              const matchingOsceTesterTuples = examDay.osceTesterTuples.filter(
                osceTuple => osceTuple.osceGroupID === groupedOsceTesterTuple.osceGroupID,
              );
              for (const osceTesterTuple of matchingOsceTesterTuples) {
                const index = examDay.osceTesterTuples.findIndex(osceTuple => osceTuple._id === osceTesterTuple._id);
                if (index < 0) return;
                examDay.osceTesterTuples.splice(index, 1);
              }
            }}
          ></x-button>
        </x-flex>
        ${repeat(groupedOsceTesterTuple.osceTesterTuples, (osceTesterTuple, i) => {
          // const osce =
          //   osceTesterTuple.osceID === osceTesterTuple.osce?._id
          //     ? osceTesterTuple.osce
          //     : this.remainingOsces.value?.assigned?.find(osce => osce._id === osceTesterTuple.osceID);
          return html`
            <x-flex space="sm">
              <x-text> ${prettyOsceCategory(group.osceTypes?.[i])} </x-text>
              <ils-osce-picker
                .value=${osceTesterTuple.osce}
                mode="entity"
                .examDay=${examDay}
                .allOSCEs=${this.allOSCEs.value}
                .osceFilter=${(o: OSCE) =>
                  o.osceType === group.osceTypes?.[osceTesterTuple.osceGroupIndex] &&
                  this.remainingOsces.value?.assigned.every(osce => osce._id !== o._id) &&
                  !o.archivedAt}
                @value-picked=${(e: ValueEvent<Maybe<OSCE>>) => {
                  osceTesterTuple.osceID = e.detail.value?._id ?? null;
                  osceTesterTuple.osce = e.detail.value;
                }}
              ></ils-osce-picker>
            </x-flex>
          `;
        })}
      </x-basic-card>
    `;
  }

  private _osceDropdownContent(examDay: ExamDay, osce: OSCE) {
    const warning = examDay.participatedOSCEs?.includes(osce._id);
    return html`
      <x-flex
        horizontal
        space-between
        crossaxis-center
        space="xxs"
        ${css({ whiteSpace: 'nowrap', overflow: 'hidden' })}
      >
        <x-text
          ${css({
            padding: '4px 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: warning ? this.colors.tone.warning : undefined,
          })}
        >
          ${osce?.title}
        </x-text>
        ${warning
          ? html`
              <x-icon ${css({ color: this.colors.tone.warning, marginRight: '0.2em' })}> warning </x-icon>
              <x-tooltip
                .text=${html`
                  <x-text> </x-text>
                  <x-text> Ein Prüfling hat an diesem OSCE bereits teilgenommen </x-text>
                `}
              ></x-tooltip>
            `
          : html` <ils-osce-type .osce=${osce}></ils-osce-type> `}
      </x-flex>
    `;
  }

  private _osceGroupDropdownContent(examDay: ExamDay, osceGroup: OSCEGroup) {
    return html`
      <x-flex
        horizontal
        space-between
        crossaxis-center
        space="xxs"
        ${css({ whiteSpace: 'nowrap', overflow: 'hidden' })}
      >
        <x-text
          ${css({
            padding: '4px 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          })}
        >
          ${osceGroup.name}
        </x-text>
      </x-flex>
    `;
  }
}
